/**
 * PersonaBubble -- cinematic voice-AI presence orb.
 *
 * Pure CSS / inline-keyframes -- no WebGL dependency. Layers:
 *   1. Outer aura       (huge blurred radial halo, drives the "presence")
 *   2. Slow conic ring  (rotates a colored ring like a halo)
 *   3. Counter-spin ring (subtle, gives depth)
 *   4. Core orb         (glassy gradient sphere, breathing scale)
 *   5. Inner shimmer    (faint moving highlight band)
 *   6. Particle field   (a few drifting micro-dots)
 *
 * `status` drives the speed: 'live' breathes/rotates faster, 'reconnecting'
 * slows down and desaturates, 'idle' is calm.
 *
 * `gradient` is a Tailwind gradient stop string (e.g. "from-pink-500 via-rose-400 to-orange-400")
 * coming straight from the Persona library so it stays in sync everywhere.
 */
import React, { useMemo } from 'react';

type BubbleStatus = 'live' | 'reconnecting' | 'idle';

interface PersonaBubbleProps {
  gradient: string;
  size?: number; // px, defaults to 280
  status?: BubbleStatus;
  speaking?: boolean;
  /** 0..1 -- live audio amplitude from the AI voice. Drives the core scale. */
  amplitude?: number;
  className?: string;
}

const PersonaBubble: React.FC<PersonaBubbleProps> = ({
  gradient,
  size = 280,
  status = 'idle',
  speaking = false,
  amplitude = 0,
  className,
}) => {
  // Try to derive a representative color from the Tailwind gradient string for
  // shadows / particle tint. We do a coarse mapping; defaults are pink.
  const accent = useMemo(() => extractAccent(gradient), [gradient]);

  const speedScale = status === 'live' ? 1 : status === 'reconnecting' ? 1.8 : 1.3;
  const opacityAura = status === 'reconnecting' ? 0.4 : status === 'live' ? 0.95 : 0.7;

  // Amplitude-driven scale on top of the breathing animation -- gives the
  // "the orb is literally speaking" feeling. Clamp to a sane range.
  const ampScale = 1 + Math.min(amplitude, 1) * 0.12;

  return (
    <div
      className={`relative inline-block ${className || ''}`}
      style={{ width: size, height: size, ['--accent' as any]: accent }}
    >
      <style>{KEYFRAMES}</style>

      {/* Layer 1: Outer aura -- huge halo, the "presence" of the orb */}
      <div
        aria-hidden
        className={`absolute rounded-full bg-gradient-to-br ${gradient}`}
        style={{
          inset: '-30%',
          filter: 'blur(60px) saturate(140%)',
          opacity: opacityAura,
          animation: `bubble-pulse ${5 * speedScale}s ease-in-out infinite`,
        }}
      />

      {/* Layer 2: Slow conic-gradient ring */}
      <div
        aria-hidden
        className="absolute rounded-full"
        style={{
          inset: 0,
          background: `conic-gradient(from 0deg, transparent 0%, ${accent} 18%, transparent 35%, ${accent}88 55%, transparent 75%, ${accent} 92%, transparent 100%)`,
          filter: 'blur(8px) saturate(160%)',
          opacity: 0.85,
          animation: `bubble-spin ${14 * speedScale}s linear infinite`,
        }}
      />

      {/* Layer 3: Counter-spin ring (depth) */}
      <div
        aria-hidden
        className="absolute rounded-full"
        style={{
          inset: '8%',
          background: `conic-gradient(from 90deg, transparent 0%, #ffffff55 12%, transparent 30%, transparent 70%, #ffffff35 88%, transparent 100%)`,
          mixBlendMode: 'overlay',
          animation: `bubble-spin-reverse ${22 * speedScale}s linear infinite`,
        }}
      />

      {/* Layer 4: Core orb -- glassy sphere */}
      <div
        className={`absolute rounded-full bg-gradient-to-br ${gradient} overflow-hidden`}
        style={{
          inset: '16%',
          boxShadow: `
            inset 0 -25% 60px rgba(0,0,0,0.35),
            inset 0 25% 50px rgba(255,255,255,0.18),
            0 25px 60px -10px rgba(0,0,0,0.55),
            0 0 ${60 + amplitude * 80}px -10px ${accent}
          `,
          transform: `scale(${ampScale})`,
          transition: 'transform 80ms ease-out, box-shadow 120ms ease-out',
          animation: `bubble-breathe ${(speaking ? 1.4 : 3.6) * (status === 'live' ? 1 : 1.6)}s ease-in-out infinite`,
        }}
      >
        {/* Layer 4a: Top highlight (the "wet" sheen) */}
        <div
          aria-hidden
          className="absolute"
          style={{
            top: '4%',
            left: '12%',
            right: '12%',
            height: '38%',
            borderRadius: '50%',
            background: 'radial-gradient(ellipse at 50% 30%, rgba(255,255,255,0.55), rgba(255,255,255,0) 65%)',
            filter: 'blur(2px)',
          }}
        />

        {/* Layer 4b: Bottom shadow */}
        <div
          aria-hidden
          className="absolute"
          style={{
            bottom: '0',
            left: '0',
            right: '0',
            height: '55%',
            background: 'radial-gradient(ellipse at 50% 100%, rgba(0,0,0,0.4), rgba(0,0,0,0) 60%)',
          }}
        />

        {/* Layer 5: Inner shimmer band (slow drift) */}
        <div
          aria-hidden
          className="absolute"
          style={{
            inset: '-25%',
            background:
              'linear-gradient(115deg, transparent 35%, rgba(255,255,255,0.18) 50%, transparent 65%)',
            animation: `bubble-shimmer ${(speaking ? 4 : 8) * speedScale}s ease-in-out infinite`,
          }}
        />
      </div>

      {/* Layer 6: Floating micro-particles around the orb */}
      {PARTICLES.map((p, i) => (
        <span
          key={i}
          aria-hidden
          className="absolute rounded-full"
          style={{
            top: `${p.top}%`,
            left: `${p.left}%`,
            width: `${p.size}px`,
            height: `${p.size}px`,
            background: accent,
            boxShadow: `0 0 ${p.size * 2.5}px ${accent}`,
            opacity: p.opacity,
            animation: `bubble-drift ${(p.duration) * speedScale}s ease-in-out ${p.delay}s infinite`,
          }}
        />
      ))}

      {/* Layer 7: Subtle bright ring on top for definition */}
      <div
        aria-hidden
        className="absolute rounded-full pointer-events-none"
        style={{
          inset: '16%',
          boxShadow: `inset 0 0 0 1px rgba(255,255,255,0.35)`,
        }}
      />
    </div>
  );
};

// ============================================================
// Helpers
// ============================================================

/**
 * Crude tailwind-gradient -> hex accent extraction. Used to colour the
 * particles, halo glow, and conic ring with something that matches the
 * persona's gradient (so e.g. Erik's blue-cyan bubble isn't surrounded
 * by pink particles).
 *
 * Priority: via-* > from-* > to-*. The via-stop sits in the middle of
 * a 3-stop gradient and is usually the most visually dominant -- using
 * it as the accent keeps halo / particles harmonised with the orb.
 *
 * IMPORTANT: every Tailwind colour used in any persona's `gradient`
 * field in personas.py MUST appear in this map -- a missing colour
 * falls through to the neutral slate default, which silently looks
 * "wrong" but no longer flashes pink. When adding a new persona,
 * add its colours here too.
 */
function extractAccent(gradient: string): string {
  const map: Record<string, string> = {
    // pink / rose family
    'pink-500': '#ec4899',
    'pink-400': '#f472b6',
    'rose-400': '#fb7185',
    // amber / orange family
    'amber-500': '#f59e0b',
    'orange-400': '#fb923c',
    // blue / indigo family
    'blue-600': '#2563eb',
    'blue-500': '#3b82f6',
    'indigo-400': '#818cf8',
    // purple / violet family
    'violet-400': '#a78bfa',
    'purple-500': '#a855f7',
    'purple-400': '#c084fc',
    'purple-300': '#d8b4fe',
    // cyan / teal family
    'cyan-500': '#06b6d4',
    'cyan-400': '#22d3ee',
    'teal-400': '#2dd4bf',
    'emerald-500': '#10b981',
    // neutrals
    'slate-500': '#64748b',
    'slate-400': '#94a3b8',
  };
  const colourKeys = Object.keys(map);
  for (const prefix of ['via-', 'from-', 'to-']) {
    for (const key of colourKeys) {
      if (gradient.includes(`${prefix}${key}`)) return map[key];
    }
  }
  return '#64748b';
}

const PARTICLES = [
  { top: 4,  left: 18, size: 3, opacity: 0.7, duration: 5,  delay: 0.0 },
  { top: 12, left: 88, size: 2, opacity: 0.6, duration: 6,  delay: 0.6 },
  { top: 72, left: 92, size: 4, opacity: 0.8, duration: 7,  delay: 1.2 },
  { top: 88, left: 30, size: 3, opacity: 0.6, duration: 6,  delay: 1.6 },
  { top: 38, left: 4,  size: 2, opacity: 0.8, duration: 8,  delay: 0.4 },
  { top: 22, left: 50, size: 2, opacity: 0.7, duration: 9,  delay: 2.0 },
  { top: 80, left: 60, size: 2, opacity: 0.6, duration: 7,  delay: 1.4 },
];

// We inject keyframes inline so the component is drop-in (no global CSS edit).
const KEYFRAMES = `
@keyframes bubble-spin           { from { transform: rotate(0deg); }   to { transform: rotate(360deg); } }
@keyframes bubble-spin-reverse   { from { transform: rotate(0deg); }   to { transform: rotate(-360deg); } }
@keyframes bubble-pulse          {
  0%, 100% { transform: scale(0.95); opacity: 0.85; }
  50%      { transform: scale(1.08); opacity: 1;    }
}
@keyframes bubble-breathe        {
  0%, 100% { transform: scale(1);    }
  50%      { transform: scale(1.04); }
}
@keyframes bubble-shimmer        {
  0%   { transform: translate(-30%, -20%) rotate(0deg); }
  50%  { transform: translate( 30%,  20%) rotate(180deg); }
  100% { transform: translate(-30%, -20%) rotate(360deg); }
}
@keyframes bubble-drift          {
  0%, 100% { transform: translate(0px, 0px);   opacity: var(--p-op, 0.6); }
  50%      { transform: translate(6px, -8px);  opacity: 1;                }
}
`;

export default PersonaBubble;
